import React from "react";
import { useTranslation } from "react-i18next";
import { Order } from "@/api/models/api-types";
import { OrderDetailProductCard } from "../components/OrderDetailProductCard";
import { FileText, AlertCircle, Truck, ExternalLink } from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import { getStatusConfig } from "../utils/get-status-config";

interface OrderStatusSectionsProps {
  orders: Order[];
}

// Failure reason code → i18n key mapping
const FAILURE_REASON_KEYS: Record<string, string> = {
  insufficient_funds: "orders.failureReason.insufficient_funds",
  invalid_card_number: "orders.failureReason.invalid_card_number",
  invalid_expiry_date: "orders.failureReason.invalid_expiry_date",
  invalid_cvc: "orders.failureReason.invalid_cvc",
  card_blocked: "orders.failureReason.card_blocked",
  payment_declined: "orders.failureReason.payment_declined",
  threeds_failed: "orders.failureReason.threeds_failed",
  fraud_suspected: "orders.failureReason.fraud_suspected",
  session_expired: "orders.failureReason.session_expired",
  payment_failed: "orders.failureReason.payment_failed",
};

export const OrderStatusSections: React.FC<OrderStatusSectionsProps> = ({
  orders,
}) => {
  const { t } = useTranslation();

  const getFailureMessage = (
    failureReason: { code: string; message: string } | undefined,
  ): string | null => {
    if (!failureReason) return null;
    const key = FAILURE_REASON_KEYS[failureReason.code];
    if (key) {
      const translated = t(key);
      // t() returns the key itself if translation is missing — fall back to API message
      if (translated && translated !== key) return translated;
    }
    return failureReason.message || null;
  };

  return (
    <div className="space-y-6">
      {orders.map((order) => {
        const statusConfig = getStatusConfig(order.status, t);
        const isFailed = String(order.status).toLowerCase() === "failed";
        const failureMessage = getFailureMessage(order.failureReason);

        const hasTracking = !!order.trackingNumber || !!order.trackingUrl;
        const hasInvoice  = !!order.invoiceLink;
        const hasOrderLink = !!order.orderLink;

        return (
          <div
            key={order._id}
            className="rounded-[12px] border border-border-primary dark:border-gray-700 overflow-hidden"
          >
            {/* Section Header */}
            <div className="bg-bg-primary dark:bg-gray-800/50 border-b border-border-primary dark:border-gray-700 px-3 md:px-4 py-3">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-0">
                {/* Left Side: Seller & Delivery Info */}
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-12">
                  {/* Seller */}
                  <div className="flex flex-row items-center gap-2 md:flex-col md:gap-0.5 md:items-start">
                    <p className="text-[10px] md:text-xs text-text-quaternary dark:text-gray-400 whitespace-nowrap">
                      {t("orders.detail.seller")}
                    </p>
                    <a
                      href={order.merchantUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-bg-primary dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-fit"
                    >
                      {order.merchantLogoUrl && (
                        <img
                          src={order.merchantLogoUrl}
                          alt={order.merchantName}
                          className="w-3.5 h-3.5 md:w-4 md:h-4 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                      <span className="text-[10px] md:text-xs font-medium text-text-secondary dark:text-gray-300">
                        {order.merchantName}
                      </span>
                      <ArrowUpRight className="w-2.5 h-2.5 md:w-3 md:h-3 text-text-secondary dark:text-gray-300" />
                    </a>
                  </div>

                  {/* Delivery No */}
                  <div className="flex flex-row items-center gap-2 md:flex-col md:gap-0.5 md:items-start">
                    <p className="text-[10px] md:text-xs text-text-quaternary dark:text-gray-400 whitespace-nowrap">
                      {t("orders.detail.deliveryNumber")}
                    </p>
                    <p className="text-xs md:text-sm font-medium text-text-secondary dark:text-gray-200 break-all">
                      #{order.orderId}
                    </p>
                  </div>
                </div>

                {/* Right Side: Action Buttons (hide for failed orders) */}
                {!isFailed && (hasTracking || hasInvoice || hasOrderLink) && (
                  <div className="flex flex-row flex-wrap items-center gap-2 md:gap-6">
                    {/* Kargo Takip */}
                    {hasTracking && (
                      <a
                        href={order.trackingUrl || "#"}
                        target={order.trackingUrl ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-indigo-400 hover:opacity-80 transition-opacity text-xs md:text-sm"
                      >
                        <Truck className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                        <span className="font-semibold">
                          {order.trackingNumber
                            ? `${t("orders.detail.trackShipment")} #${order.trackingNumber}`
                            : t("orders.detail.trackShipment")}
                        </span>
                        {order.trackingUrl && (
                          <ExternalLink className="w-3 h-3 opacity-70" />
                        )}
                      </a>
                    )}

                    {/* Fatura */}
                    {hasInvoice && (
                      <a
                        href={order.invoiceLink!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-indigo-400 hover:opacity-80 transition-opacity text-xs md:text-sm"
                      >
                        <FileText className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                        <span className="font-semibold">
                          {t("orders.detail.viewInvoice")}
                        </span>
                        <ExternalLink className="w-3 h-3 opacity-70" />
                      </a>
                    )}

                    {/* Sipariş Linki */}
                    {hasOrderLink && (
                      <a
                        href={order.orderLink!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-indigo-400 hover:opacity-80 transition-opacity text-xs md:text-sm"
                      >
                        <ArrowUpRight className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                        <span className="font-semibold">
                          {t("orders.detail.viewOrder")}
                        </span>
                      </a>
                    )}

                    {/* İade Talebi — henüz hazır değil */}
                    {/* <button
                      className="inline-flex items-center gap-1.5 text-indigo-400 hover:opacity-80 transition-opacity text-xs md:text-sm"
                      onClick={() => {}}
                    >
                      <RotateCcw className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                      <span className="font-semibold">
                        {t("orders.detail.createReturnRequest")}
                      </span>
                    </button> */}
                  </div>
                )}
              </div>
            </div>

            {/* Section Content */}
            <div className="p-3 md:p-4 flex flex-col gap-3 md:gap-4">
              {/* Status Badge + Description */}
              <div className="flex flex-col gap-2">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md w-fit ${statusConfig.badgeClass}`}
                  >
                    {statusConfig.icon}
                    <span className="text-xs md:text-sm font-medium">
                      {statusConfig.label}
                    </span>
                  </div>

                </div>

                {/* Failure reason banner */}
                {isFailed && failureMessage && (
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs md:text-sm text-red-700 dark:text-red-400">
                      {failureMessage}
                    </p>
                  </div>
                )}
              </div>

              {/* Products Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {order.items.map((item, itemIndex) => (
                  <OrderDetailProductCard
                    key={`${order._id}-${itemIndex}`}
                    item={item}
                    currency={order.currency}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
